/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

'use client';
import React, { useEffect, useState, useMemo } from 'react';
import { View, StyleSheet } from 'react-native';
import { getColor, pd } from '@airasia-phoenix/react-native/utils/utils';
import HydrationSpinner from './HydrationSpinner';
import {
  AIRASIA_MOVE_LOGO_DATA_URI,
  AIRASIA_MOVE_LOGO_HEIGHT,
  AIRASIA_MOVE_LOGO_WIDTH,
} from './airasiaMoveLogo';

// Derived from the logo's intrinsic size so the splash keeps its aspect ratio
// and reserves its box up front — a mis-sized splash is the one thing users
// notice on a screen that exists purely to look calm while hydration happens.
const SPLASH_WIDTH = 200;
const SPLASH_HEIGHT = Math.round(
  (SPLASH_WIDTH * AIRASIA_MOVE_LOGO_HEIGHT) / AIRASIA_MOVE_LOGO_WIDTH,
);

interface HydrationLoaderProps {
  env?: string;
  useCssLoader?: boolean;
  /**
   * Render the inlined AirAsia MOVE logo as a splash instead of a spinner.
   * The logo ships as a base64 data URI, so it paints immediately rather than
   * waiting on a network request during the pre-hydration window.
   */
  useSplashImage?: boolean;
  delay?: number;
}

export default function HydrationLoader({
  env,
  useCssLoader = false,
  useSplashImage = false,
  delay = 0,
}: HydrationLoaderProps) {
  const [hidden, setHidden] = useState(false);
  const host = useMemo(
    () =>
      env === 'prd'
        ? 'https://static.airasia.com/mobile/assets/circle-loader.webm'
        : 'https://static.stgairasia.com/mobile/assets/circle-loader.webm',
    [env],
  );

  useEffect(() => {
    if (delay <= 0) {
      setHidden(true);
      return;
    }
    const id = setTimeout(() => setHidden(true), delay);
    return () => clearTimeout(id);
  }, [delay]);

  if (hidden) return null;

  const renderIndicator = () => {
    if (useSplashImage) {
      return (
        <img
          src={AIRASIA_MOVE_LOGO_DATA_URI}
          alt="AirAsia MOVE"
          width={SPLASH_WIDTH}
          height={SPLASH_HEIGHT}
          style={{
            height: SPLASH_HEIGHT,
            width: SPLASH_WIDTH,
          }}
        />
      );
    }

    if (useCssLoader) {
      return <HydrationSpinner />;
    }

    return (
      <video
        src={host}
        autoPlay
        key="hydration-loader"
        loop
        muted
        playsInline
        style={{
          height: 70,
          width: 70,
        }}
      />
    );
  };

  return (
    <View style={styles.overlayStyle} testID="hydration-loader">
      {renderIndicator()}
    </View>
  );
}

const styles = StyleSheet.create({
  overlayStyle: {
    alignItems: 'center',
    backgroundColor: getColor(pd.sys.colour.container_none),
    display: 'flex',
    inset: 0,
    justifyContent: 'center',
    position: 'absolute',
    zIndex: 9999,
  },
});
